import { CopyIcon } from 'lucide-react';
import { type JSX } from 'react';

import type { CaseDetail } from '@/lib/api/quality-types.js';
import { notify } from '@/lib/notify.js';
import { cn } from '@/lib/utils.js';

import { platformComponentIcon } from '../quality-presentation.js';
import {
  caseClipStartMs,
  caseLabel,
  knownSessionCases,
  wholeRecordingSelection,
  type Recording,
} from '../player/recordings.js';

/** Copies a BrowserStack session id with the repo's toast idiom (see `DeckDetail.tsx`,
 *  `PlayerDock.tsx`). */
async function copySessionId(sessionId: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(sessionId);
    notify.success('Session id copied.');
  } catch {
    notify.error('Clipboard unavailable');
  }
}

/**
 * The Video tab: one player (the shared `PlayerDock`, rendered once by `QualityRunDetail` as a
 * sibling after `<Tabs>`) plus this selector of the run's recordings and the cases they cover — no
 * stacked per-entry players, no refs, no scroll effect. Selecting a row is the load gesture; the
 * caller (`QualityRunDetail`) binds the dock, sends its explicit load command, and seeks when a
 * clip window resolves.
 */
export function VideoReport({
  cases,
  recordings,
  boundKey,
  onSelect,
}: {
  cases?: CaseDetail[];
  recordings: Recording[];
  boundKey: string | null;
  /** `caseId` scopes the docked chapters list (S9) to the case this selection belongs to — omitted
   *  only for an unattributed mobile session (AC 17), which has no case to scope to. */
  onSelect: (
    recording: Recording,
    seekMs: number | null,
    caseId?: string,
  ) => void;
}): JSX.Element {
  if (recordings.length === 0) {
    return (
      <p className="px-4 py-8 text-center text-sm text-muted-foreground">
        This run has no recorded video.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {recordings.map((recording) => (
        <VideoRow
          key={recording.key}
          recording={recording}
          cases={cases}
          active={recording.key === boundKey}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function VideoRow({
  recording,
  cases,
  active,
  onSelect,
}: {
  recording: Recording;
  cases?: CaseDetail[];
  active: boolean;
  onSelect: (
    recording: Recording,
    seekMs: number | null,
    caseId?: string,
  ) => void;
}): JSX.Element {
  const Icon = platformComponentIcon(recording.kind);
  /** A session covering several cases (the norm is 1:1 — see the S1 data reality note) exposes each
   *  case as its own selectable target (AC 16), rather than only landing at the session's start.
   *  Counts only cases this report actually carries (finding 3) — an id the session lists that isn't
   *  in this report (AC 17) must never turn into a selectable pill. */
  const knownCases =
    recording.kind === 'mobile'
      ? knownSessionCases(recording.caseIds ?? [], cases)
      : [];
  const showCaseList = knownCases.length > 1;
  /** The row's own unambiguous case (and where selecting the recording as a whole should seek) — a
   *  web recording always carries exactly one, as does a single-case mobile session; undefined for a
   *  multi-case session (which instead exposes its cases as sub-rows below) or an unattributed
   *  session (AC 17 — its `case_ids` names a case that isn't in this report, so there is nothing
   *  real to scope chapters to; never invent one). */
  const { seekMs: wholeSeekMs, caseId: soleCaseId } = wholeRecordingSelection(
    recording,
    cases,
  );

  /** The row's icon + label (+ pending badge) is the "select this recording" gesture — a <button>
   *  for a single-case (or web) row, a plain label when the row instead exposes a per-case sub-list
   *  (buttons can't nest, so the copy-session-id control below is always a SIBLING, never a child). */
  const iconAndLabel = (
    <>
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1 truncate font-medium">
        {recording.label}
      </span>
      {recording.pending ? (
        <span className="shrink-0 text-xs text-muted-foreground">
          {recording.unavailable
            ? 'No recording'
            : recording.kind === 'mobile'
              ? 'Recording…'
              : 'Processing…'}
        </span>
      ) : null}
    </>
  );

  return (
    <div
      aria-current={active ? 'true' : undefined}
      className={cn(
        'rounded-lg border border-border p-3 transition-colors',
        active && 'border-primary bg-primary/5',
      )}
    >
      <div className="flex items-center gap-2">
        {showCaseList ? (
          <div className="flex min-w-0 flex-1 items-center gap-2 text-sm">
            {iconAndLabel}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onSelect(recording, wholeSeekMs, soleCaseId)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm"
          >
            {iconAndLabel}
          </button>
        )}
        {/* The session id is support-desk material, not the row's identity — spelled out it took
            ~300px of every row and was the only thing that varied, so the eye landed on noise while
            the case number was nowhere to be seen. Now the label carries the case number and this
            keeps the id one click (and one tooltip) away. */}
        {recording.kind === 'mobile' && recording.sessionId ? (
          <button
            type="button"
            onClick={() => void copySessionId(recording.sessionId!)}
            className="inline-flex shrink-0 items-center rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Copy BrowserStack session id"
            title={`Copy BrowserStack session id (${recording.sessionId})`}
          >
            <CopyIcon className="size-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      {showCaseList ? (
        <div className="mt-2 flex flex-wrap gap-1.5 pl-6">
          {knownCases.map((caseDetail) => (
            <button
              key={caseDetail.id}
              type="button"
              onClick={() =>
                onSelect(
                  recording,
                  caseClipStartMs(caseDetail, recording.key),
                  caseDetail.id,
                )
              }
              className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
            >
              {caseLabel(caseDetail)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
